/**
 * Notify Me: a shopper asks to hear when a sold-out product, colour or model
 * is back. Any button with [data-notify] and data-variant-id starts it.
 *
 * Signed in, the request goes straight away under the account's email (the
 * layout puts it on window.OracuraNotifyConfig); otherwise a small dialog
 * asks for one. The button then says the request is in, and the browser
 * remembers it so the card still says so on the next visit.
 *
 * Who sends the email when stock returns is the back-in-stock app's job, so
 * the request goes to whichever is set up, in this order:
 *   1. Appikon "Back in Stock: Restock Alerts" — its storefront script puts
 *      BIS on the page; BIS.create(email, variantId, productId) saves it.
 *   2. Klaviyo — with its public API key in Theme settings > Back in stock,
 *      its back-in-stock subscription endpoint.
 *   3. Neither — the store's own contact form, so the request lands in the
 *      store's inbox for someone to answer when the product is back.
 * Theme settings > Back in stock can pin one of them instead of the first
 * found.
 */
(() => {
  const config = window.OracuraNotifyConfig || {};
  const L = Object.assign({
    heading: 'Get notified',
    text: 'We will email you as soon as this is back in stock.',
    placeholder: 'Your email address',
    submit: 'Notify Me',
    sending: 'Sending…',
    done: 'You are on the list',
    doneText: 'We will email [email] when it is back.',
    notified: 'We will email you',
    invalid: 'Please enter a valid email address.',
    error: 'Something went wrong. Please try again.',
    close: 'Close',
    consent: '',
  }, config.labels || {});
  // Liquid's t filter hands its strings over HTML-escaped ("We&#39;ll"), and
  // these are set as text, not markup, so they are decoded once here.
  const decoder = document.createElement('textarea');
  Object.keys(L).forEach((key) => {
    decoder.innerHTML = String(L[key]);
    L[key] = decoder.value;
  });
  const KEY = 'oracura-notify';

  const remembered = () => {
    try { return JSON.parse(localStorage.getItem(KEY) || '{}'); } catch (e) { return {}; }
  };
  const remember = (variantId) => {
    try {
      const all = remembered();
      all[variantId] = Date.now();
      localStorage.setItem(KEY, JSON.stringify(all));
    } catch (e) { /* private mode: it only means the card forgets */ }
  };

  const validEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(email).trim());

  /* ---------------------------------------------------------------- sending */

  async function viaAppikon(request) {
    const bis = window.BIS;
    if (!bis || typeof bis.create !== 'function') return null;
    const result = await bis.create(request.email, Number(request.variantId), Number(request.productId));
    if (result && result.status && result.status !== 'OK') throw new Error(result.errors ? JSON.stringify(result.errors) : 'Appikon');
    return 'appikon';
  }

  async function viaKlaviyo(request) {
    if (!config.klaviyoKey) return null;
    const response = await fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' + encodeURIComponent(config.klaviyoKey), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
      body: JSON.stringify({
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            profile: { data: { type: 'profile', attributes: { email: request.email } } },
            channels: ['EMAIL'],
          },
          relationships: {
            variant: { data: { type: 'catalog-variant', id: '$shopify:::$default:::' + request.variantId } },
          },
        },
      }),
    });
    if (!response.ok) throw new Error('Klaviyo ' + response.status);
    return 'klaviyo';
  }

  async function viaContact(request) {
    const body = new URLSearchParams();
    body.set('form_type', 'contact');
    body.set('utf8', '✓');
    body.set('contact[email]', request.email);
    body.set('contact[name]', request.name || '');
    body.set('contact[body]', [
      'Back-in-stock request',
      'Product: ' + request.productTitle,
      'Variant: ' + (request.variantTitle || '-') + ' (' + request.variantId + ')',
      'Page: ' + location.origin + (request.url || ''),
      'Email: ' + request.email,
    ].join('\n'));
    const response = await fetch('/contact', { method: 'POST', body, headers: { Accept: 'text/html' } });
    // Shopify answers a sent form by redirecting to ?contact_posted=true; a
    // spam check sends the browser to /challenge instead.
    if (!response.ok || /\/challenge/.test(response.url) || !/contact_posted=true/.test(response.url)) {
      throw new Error('contact ' + response.status);
    }
    return 'contact';
  }

  async function send(request) {
    const order = { appikon: [viaAppikon], klaviyo: [viaKlaviyo], contact: [viaContact] }[config.provider]
      || [viaAppikon, viaKlaviyo, viaContact];
    for (const attempt of order) {
      const used = await attempt(request);
      if (used) return used;
    }
    throw new Error('No back-in-stock service');
  }

  /* ---------------------------------------------------------------- the button */

  function markDone(button) {
    button.classList.add('is-notified');
    button.disabled = true;
    const text = button.querySelector('[data-notify-text]') || button;
    text.textContent = L.notified;
  }

  function requestFrom(button) {
    return {
      variantId: button.dataset.variantId,
      productId: button.dataset.productId,
      productTitle: button.dataset.productTitle || '',
      variantTitle: button.dataset.variantTitle || '',
      url: button.dataset.url || '',
    };
  }

  /* ---------------------------------------------------------------- the dialog */

  let dialog;
  let pending;

  function buildDialog() {
    dialog = document.createElement('dialog');
    dialog.className = 'oracura-notify';
    dialog.setAttribute('aria-labelledby', 'oracura-notify-title');
    dialog.innerHTML = `
      <form class="oracura-notify__panel" method="dialog" novalidate>
        <button type="button" class="oracura-notify__close" data-notify-close aria-label=""><span aria-hidden="true">×</span></button>
        <div class="oracura-notify__ask">
          <p class="oracura-notify__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.7 21a2 2 0 0 1-3.4 0"/></svg>
          </p>
          <h2 class="oracura-notify__title" id="oracura-notify-title"></h2>
          <p class="oracura-notify__product" data-notify-product></p>
          <p class="oracura-notify__text"></p>
          <label class="visually-hidden" for="oracura-notify-email"></label>
          <input class="oracura-notify__input" id="oracura-notify-email" type="email" name="email" autocomplete="email" inputmode="email" required>
          <p class="oracura-notify__error" data-notify-error role="alert" hidden></p>
          <button type="submit" class="oracura-notify__submit" data-notify-submit></button>
          <p class="oracura-notify__consent" data-notify-consent></p>
        </div>
        <div class="oracura-notify__thanks" hidden>
          <p class="oracura-notify__icon oracura-notify__icon--done" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>
          </p>
          <h2 class="oracura-notify__title" data-notify-done-title></h2>
          <p class="oracura-notify__text" data-notify-done-text></p>
        </div>
      </form>`;
    dialog.querySelector('.oracura-notify__close').setAttribute('aria-label', L.close);
    dialog.querySelector('.oracura-notify__ask .oracura-notify__title').textContent = L.heading;
    dialog.querySelector('.oracura-notify__ask .oracura-notify__text').textContent = L.text;
    dialog.querySelector('label[for="oracura-notify-email"]').textContent = L.placeholder;
    dialog.querySelector('.oracura-notify__input').placeholder = L.placeholder;
    dialog.querySelector('[data-notify-submit]').textContent = L.submit;
    const consent = dialog.querySelector('[data-notify-consent]');
    consent.textContent = L.consent;
    consent.hidden = !L.consent;
    dialog.querySelector('[data-notify-done-title]').textContent = L.done;

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog || event.target.closest('[data-notify-close]')) dialog.close();
    });
    dialog.querySelector('form').addEventListener('submit', async (event) => {
      event.preventDefault();
      const input = dialog.querySelector('.oracura-notify__input');
      const error = dialog.querySelector('[data-notify-error]');
      const submit = dialog.querySelector('[data-notify-submit]');
      const email = input.value.trim();
      if (!validEmail(email)) {
        error.textContent = L.invalid;
        error.hidden = false;
        input.focus();
        return;
      }
      error.hidden = true;
      submit.disabled = true;
      submit.textContent = L.sending;
      try {
        await send({ ...pending.request, email });
        remember(pending.request.variantId);
        markDone(pending.button);
        try { localStorage.setItem(KEY + '-email', email); } catch (e) { /* fine */ }
        dialog.querySelector('[data-notify-done-text]').textContent = L.doneText.replace('[email]', email);
        dialog.querySelector('.oracura-notify__ask').hidden = true;
        dialog.querySelector('.oracura-notify__thanks').hidden = false;
      } catch (err) {
        error.textContent = L.error;
        error.hidden = false;
      } finally {
        submit.disabled = false;
        submit.textContent = L.submit;
      }
    });
    document.body.append(dialog);
  }

  function openDialog(button, request) {
    if (!dialog) buildDialog();
    pending = { button, request };
    dialog.querySelector('.oracura-notify__ask').hidden = false;
    dialog.querySelector('.oracura-notify__thanks').hidden = true;
    dialog.querySelector('[data-notify-error]').hidden = true;
    dialog.querySelector('[data-notify-product]').textContent = [request.productTitle, request.variantTitle].filter(Boolean).join(' – ');
    const input = dialog.querySelector('.oracura-notify__input');
    try { input.value = localStorage.getItem(KEY + '-email') || ''; } catch (e) { input.value = ''; }
    dialog.showModal();
    input.focus();
  }

  function toast(message) {
    let el = document.querySelector('[data-oracura-cart-feedback]');
    if (!el) {
      el = document.createElement('p');
      el.className = 'oracura-cart-feedback';
      el.dataset.oracuraCartFeedback = '';
      el.setAttribute('role', 'status');
      document.body.append(el);
    }
    el.textContent = message;
    el.hidden = false;
    clearTimeout(toast.timer);
    toast.timer = setTimeout(() => { el.hidden = true; }, 5000);
  }

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-notify]');
    if (!button || button.disabled) return;
    event.preventDefault();
    event.stopPropagation();
    const request = requestFrom(button);
    if (!request.variantId) return;
    if (config.email) {
      // Signed in: the account's email, no questions.
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      try {
        await send({ ...request, email: config.email, name: config.name || '' });
        remember(request.variantId);
        markDone(button);
        toast(L.doneText.replace('[email]', config.email));
      } catch (err) {
        button.disabled = false;
        toast(L.error);
      } finally {
        button.removeAttribute('aria-busy');
      }
      return;
    }
    openDialog(button, request);
  });

  // A button for a variant already asked about says so.
  const sync = (scope = document) => {
    const done = remembered();
    scope.querySelectorAll('[data-notify]').forEach((button) => {
      if (done[button.dataset.variantId]) markDone(button);
    });
  };
  window.OracuraNotify = { sync, send, isNotified: (id) => Boolean(remembered()[id]), markDone, labels: L };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => sync());
  else sync();
})();
